/**
 * 기업별 사용량 — 고객이 무엇을 얼마나 쓰고 있나
 * ---------------------------------------------------------------------------
 * ## 왜 화면으로 뺐나
 * 이 표는 「시스템 상태」 맨 아래 카드로만 있었다. 거기는 CPU·메모리·디스크를
 * 보러 가는 자리라, **어느 고객이 한도에 가까운가** 를 알고 싶을 때마다 서버
 * 지표를 지나쳐 한참 스크롤해야 했다. 묻는 질문이 다르므로 자리도 나눈다.
 *
 * ## 한도는 계약에서 온다
 * 예전에는 사용량과 한도를 모두 metering.quota_state 에서 읽었다. 그 표는
 * **사용량이 한 번이라도 움직였을 때만 행이 생기는 캐시**라, 아직 파일을
 * 안 올린 기업이나 이번 달에 내려받은 적이 없는 기업은 행이 없어
 * 「0B / 한도 없음」으로 떴다 — 요금제에는 16GB 가 분명히 걸려 있는데도.
 * 지금은 서버가 계약(plan_quota × 계약 수량, 기업별 예외 반영)에서 한도를
 * 계산해 내려 준다.
 *
 * ## 숫자가 언제 갱신되나
 * 저장 용량·파일 수는 파일을 올리고 지울 때 **그 자리에서** 더하고 뺀다
 * (storage.service). 전송량·AI 보고서도 쓰는 즉시 쌓인다.
 * 그와 별개로 **매일 새벽 3시**에 원장(파일 표·계정 표)에서 전부 다시 세어
 * 덮어쓴다(UsageRollupService) — 중간에 어긋난 것이 있으면 그때 맞춰진다.
 *
 * ## 파일 수를 용량과 나란히 두는 이유
 * 같은 1GB 라도 큰 파일 열 개와 작은 파일 십만 개는 백업·이관·삭제에 드는
 * 품이 전혀 다르다. 한도는 용량에 걸리지만, **일이 얼마나 되는지**는 개수를
 * 봐야 안다.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Card,
  Col,
  Input,
  Progress,
  Radio,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { PageHelp } from '../../components/PageHelp';
import { GUIDE } from '../../lib/guide';

const { Text } = Typography;

interface TenantUsage {
  tenantId: string;
  companyName: string;
  slug: string;
  status: string;
  userCount: number;
  activeUserCount: number;
  storageBytes: number;
  storageLimit: number | null;
  /** 보관 중인 파일 개수. 용량과 짝으로 읽는다 */
  fileCount: number;
  egressBytes: number;
  egressLimit: number | null;
  aiReports: number;
  dbBytes: number;
  plans: string | null;
  exceededMetrics: string[];
  lastLoginAt: string | null;
}

type Sort = 'storage' | 'egress' | 'users';

/** 한 쪽에 몇 줄. 고객이 늘어도 이 화면이 끝없이 길어지지 않게 한다. */
const PAGE_SIZES = ['10', '20', '50', '100'];

export default function TenantUsagePage() {
  const [sort, setSort] = useState<Sort>('storage');
  const [keyword, setKeyword] = useState('');
  /*
    쪽 번호를 들고 있는 이유.
    3쪽을 보던 중에 검색어를 넣으면 결과가 한 쪽뿐인데 여전히 3쪽을 보여
    **빈 표**가 뜬다. 거르는 조건이 바뀌면 첫 쪽으로 되돌린다.
  */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const tenants = useQuery<TenantUsage[], NormalizedError>({
    queryKey: ['admin', 'tenant-usage', sort],
    queryFn: async () =>
      (await apiClient.get<TenantUsage[]>('/admin/dashboard/tenants', { params: { sort } })).data,
  });

  const rows = (tenants.data ?? []).filter((r) => {
    const k = keyword.trim().toLowerCase();
    if (!k) return true;
    return r.companyName.toLowerCase().includes(k) || r.slug.toLowerCase().includes(k);
  });

  /* 위쪽 요약 — 표를 보기 전에 "전체가 어떤가" 를 먼저 말해 준다. */
  const total = rows.reduce(
    (acc, r) => ({
      storage: acc.storage + r.storageBytes,
      files: acc.files + r.fileCount,
      egress: acc.egress + r.egressBytes,
      ai: acc.ai + r.aiReports,
      exceeded: acc.exceeded + (r.exceededMetrics.length > 0 ? 1 : 0),
    }),
    { storage: 0, files: 0, egress: 0, ai: 0, exceeded: 0 },
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <PageHelp title="기업별 사용량 — 화면 설명">{GUIDE['tenant-usage']?.body}</PageHelp>

      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="기업 수" value={rows.length} suffix="곳" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="저장 용량 합계" value={formatBytes(total.storage)} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              파일 {total.files.toLocaleString('ko-KR')}개
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="이번 달 전송량 합계" value={formatBytes(total.egress)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="한도 초과 기업"
              value={total.exceeded}
              suffix="곳"
              valueStyle={{ color: total.exceeded > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title="기업별 사용량"
        loading={tenants.isLoading}
        extra={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="회사명 · 식별자"
              style={{ width: 200 }}
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
            />
            <Radio.Group
              size="small"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as Sort);
                setPage(1);
              }}
              optionType="button"
              options={[
                { label: '저장 용량 순', value: 'storage' },
                { label: '전송량 순', value: 'egress' },
                { label: '구성원 순', value: 'users' },
              ]}
            />
          </Space>
        }
      >
        {tenants.isError && <Alert type="error" showIcon message={errorText(tenants.error)} />}
        <Table<TenantUsage>
          rowKey="tenantId"
          size="small"
          dataSource={rows}
          /*
            쪽 넘김은 줄 수와 상관없이 늘 둔다. 몇 곳을 보고 있는지(「N곳 중
            a–b」)가 표 아래에 항상 적혀야, 걸러낸 결과를 전체로 오해하지
            않는다. 한 쪽에 몇 줄 볼지는 보는 사람이 고른다.
          */
          pagination={{
            current: page,
            pageSize,
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size);
            },
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZES,
            size: 'small',
            showTotal: (total, [from, to]) =>
              keyword.trim()
                ? `검색 결과 ${total}곳 중 ${from}–${to}`
                : `전체 ${total}곳 중 ${from}–${to}`,
          }}
          locale={{
            emptyText: keyword.trim() ? '검색 결과가 없습니다.' : '기업이 없습니다.',
          }}
          scroll={{ x: 1250 }}
          columns={[
            {
              title: '기업',
              key: 'company',
              fixed: 'left',
              width: 220,
              render: (_, row) => (
                <Space direction="vertical" size={0}>
                  <Space size={6}>
                    <span>{row.companyName}</span>
                    {row.status !== 'ACTIVE' && <Tag>{row.status}</Tag>}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.slug}
                  </Text>
                </Space>
              ),
            },
            {
              title: '요금제',
              dataIndex: 'plans',
              width: 180,
              render: (v: string | null) =>
                v ? (
                  <Text style={{ fontSize: 12 }}>{v}</Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    구독 없음
                  </Text>
                ),
            },
            {
              title: '구성원',
              key: 'users',
              width: 100,
              align: 'right',
              render: (_, row) => `${row.activeUserCount} / ${row.userCount}명`,
            },
            {
              title: '저장 용량',
              key: 'storage',
              width: 180,
              render: (_, row) => <Quota used={row.storageBytes} limit={row.storageLimit} />,
            },
            {
              /*
                용량 바로 옆에 둔다. 둘은 같이 읽어야 뜻이 생긴다 — 같은 1GB
                라도 큰 파일 열 개와 작은 파일 십만 개는 백업·이관에 드는 품이
                전혀 다르다.
              */
              title: '파일 수',
              key: 'files',
              width: 100,
              align: 'right',
              render: (_, row) => (
                <Text style={{ fontSize: 12 }} type={row.fileCount > 0 ? undefined : 'secondary'}>
                  {row.fileCount > 0 ? `${row.fileCount.toLocaleString('ko-KR')}개` : '없음'}
                </Text>
              ),
            },
            {
              title: '이번 달 전송량',
              key: 'egress',
              width: 180,
              render: (_, row) => <Quota used={row.egressBytes} limit={row.egressLimit} />,
            },
            {
              title: 'DB 사용량',
              key: 'db',
              width: 110,
              align: 'right',
              render: (_, row) => (
                <Text style={{ fontSize: 12 }}>{formatBytes(row.dbBytes)}</Text>
              ),
            },
            {
              title: 'AI 보고서',
              key: 'ai',
              width: 100,
              align: 'right',
              render: (_, row) => (
                <Text style={{ fontSize: 12 }}>{row.aiReports.toLocaleString()}건</Text>
              ),
            },
            {
              title: '한도 초과',
              key: 'exceeded',
              width: 160,
              render: (_, row) =>
                row.exceededMetrics.length === 0 ? (
                  <Text type="secondary">-</Text>
                ) : (
                  <Space size={4} wrap>
                    {row.exceededMetrics.map((m) => (
                      <Tag color="red" key={m}>
                        {m}
                      </Tag>
                    ))}
                  </Space>
                ),
            },
            {
              title: '마지막 로그인',
              dataIndex: 'lastLoginAt',
              width: 160,
              render: (v: string | null) =>
                v ? new Date(v).toLocaleString('ko-KR') : <Text type="secondary">없음</Text>,
            },
          ]}
        />
      </Card>
    </Space>
  );
}

/**
 * 쓴 양 / 계약 한도.
 *
 * 한도가 null 이면 요금제가 그 지표를 정하지 않은 것이다 — 「한도 없음」이
 * 맞다. 예전처럼 **행이 없어서** null 이 되는 일은 없다(서버가 계약에서
 * 계산해 준다).
 */
function Quota({ used, limit }: { used: number; limit: number | null }) {
  if (!limit) {
    return (
      <Space direction="vertical" size={0}>
        <Text style={{ fontSize: 12 }}>{formatBytes(used)}</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          한도 없음
        </Text>
      </Space>
    );
  }

  const percent = Math.round((used / limit) * 100);
  return (
    <Space direction="vertical" size={0} style={{ width: '100%' }}>
      <Text style={{ fontSize: 12 }}>
        {formatBytes(used)} / {formatBytes(limit)}
      </Text>
      <Progress percent={Math.min(percent, 100)} size="small" strokeColor={levelColor(percent)} />
    </Space>
  );
}

/** 70% 노랑, 90% 빨강. 눈으로 바로 구분되어야 한다. */
function levelColor(percent: number): string {
  if (percent >= 90) return '#cf1322';
  if (percent >= 70) return '#d48806';
  return '#3f8600';
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)}${units[i]}`;
}
